import type { ContactOptionType, ContactTypeValue } from '../types';
import { IParticipantCreate } from '../../../../models/ParticipantModel';

// Constants for better maintainability
const CONTACT_TYPE_MAPPING = {
  Bhive: 'internal-contact',
  default: 'external-contact',
} as const;

const CONTACT_LABEL_MAPPING = {
  Bhive: 'Internal Contact',
  default: 'External Contact',
  unsaved: 'Phone Number',
} as const;

/**
 * Maps a contact source to its corresponding contact type value
 * @param source - The source identifier (e.g., 'Bhive' for internal contacts)
 * @returns The contact type value ('internal-contact' for Bhive, 'external-contact' for others)
 */
export const getContactType = (source: string): ContactTypeValue =>
  source === 'Bhive'
    ? CONTACT_TYPE_MAPPING.Bhive
    : CONTACT_TYPE_MAPPING.default;

/**
 * Maps a contact source to its human-readable label
 * @param source - The source identifier
 * @returns The display label
 */
export const getContactTypeLabel = (source: string): string =>
  source === 'Bhive'
    ? CONTACT_LABEL_MAPPING.Bhive
    : source === 'unsaved'
    ? CONTACT_LABEL_MAPPING.unsaved
    : CONTACT_LABEL_MAPPING.default;

/**
 * Converts contact options to IParticipantCreate format
 * @param contacts - Array of contact options
 * @returns Array of IParticipantCreate objects
 */
export const convertContactsToParticipants = (
  contacts: ContactOptionType[]
): IParticipantCreate[] => {
  const participants = contacts.map((contact) => {
    if (contact.type === 'internal-contact') {
      return { personId: parseInt(contact.value) };
    } else {
      return { phone: contact.value };
    }
  });

  return participants;
};

/**
 * Analyzes selected contacts to determine conversation rules and constraints
 * Enhanced to handle unsaved contacts properly
 */
export const analyzeSelectedContacts = (
  selectedContacts: ContactOptionType[]
) => {
  const count = selectedContacts.length;
  const firstContact = selectedContacts[0];
  const firstContactType = firstContact?.type;

  const hasInternal = selectedContacts.some(
    (contact) => contact.type === 'internal-contact'
  );
  const hasExternal = selectedContacts.some(
    (contact) => contact.type === 'external-contact'
  );
  const hasUnsaved = selectedContacts.some(
    (contact) => contact.type === 'unsaved-contact'
  );

  // Treat unsaved contacts as external for conversation type determination
  const hasAnyExternal = hasExternal || hasUnsaved;

  const isFirstContactInternal = firstContactType === 'internal-contact';
  const isMixed = hasInternal && hasAnyExternal;

  return {
    count,
    firstContactType,
    hasInternal,
    hasExternal,
    hasUnsaved,
    hasAnyExternal,
    isFirstContactInternal,
    isMixed,
  };
};

/**
 * Determines conversation type based on selected contacts and form data
 * Enhanced to handle unsaved contacts
 */
export const determineConversationType = (
  selectedContacts: ContactOptionType[],
  groupName?: string
): 'one-to-one' | 'named-group' | 'adhoc-group' | null => {
  const contactAnalysis = analyzeSelectedContacts(selectedContacts);

  if (contactAnalysis.count === 0) {
    return null;
  } else if (contactAnalysis.count === 1) {
    if (contactAnalysis.isFirstContactInternal && groupName?.trim()) {
      return 'named-group';
    } else {
      return 'one-to-one';
    }
  } else if (contactAnalysis.count > 1) {
    if (contactAnalysis.isFirstContactInternal && groupName?.trim()) {
      return 'named-group';
    } else {
      return 'adhoc-group';
    }
  }

  console.log('Returning null - fallback');
  return null;
};
